import { Box, Stack, Typography } from '@mui/material'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { EntitlementChips } from 'components/core/entitlement-chips'
import { C1TableRedux, C1TableReduxColumn } from 'components/core/list/C1Table/C1TableRedux'
import List from 'components/core/list/index'
import { useErrorReporting } from 'components/no-ui/error-reporting/datadog'
import { AppResourceCampaignFilters } from 'components/pages/accessreview/select_resources/AppResourceCampaignFilters'
import { AppDisplayName } from 'components/pages/app/app-display-name/index'
import { ResourceDisplayName } from 'components/pages/entitlement/resource/index'
import { ResourceTypeDisplayName } from 'components/pages/entitlement/resource-type/index'
import { QUERIES } from 'components/pages/explore/query/Query'
import { ResourceRollupValue } from 'lib/actions/structured_search/structured_search'
import { useInitialFilters, useSearch } from 'lib/search'
import { App } from 'pbts/c1/api/app/v1/app'
import { AppResource, AppResourceType, SearchAppResourceRollupRequest } from 'pbts/c1/api/app/v1/app_resource'
import { ExportDataRequest, QueryId } from 'pbts/c1/api/exportdata/v1/exportdata'
import { FC, useCallback, useMemo, useState } from 'react'
import { FormattedMessage, FormattedNumber } from 'react-intl'
import { appResourceSearchRollupApi } from 'redux_toolkit/app-resources'
import { ExportData } from './ExportData'

const cols: Array<C1TableReduxColumn<ResourceRollupValue>> = [
    {
        field: 'app',
        label: <FormattedMessage id="components_resource_rollup_list_app" defaultMessage="Application" />,
        Component: ({ row: resourceRollupValue }) => <AppDisplayName margin={false} app={resourceRollupValue?.app || App.create({})} />,
    },
    {
        field: 'type',
        label: <FormattedMessage id="components_resource_rollup_list_type" defaultMessage="Type" />,
        Component: ({ row: resourceRollupValue }) =>
            <ResourceTypeDisplayName
                resourceType={resourceRollupValue?.appResourceType || AppResourceType.create({})}
                margin={false}
            />,
    },
    {
        field: 'name',
        label: <FormattedMessage id="components_resource_rollup_list_resource" defaultMessage="Name" />,
        Component: ({ row: resourceRollupValue }) =>
            <ResourceDisplayName
                resource={resourceRollupValue?.appResource || AppResource.create({})}
                resourceType={resourceRollupValue?.appResourceType}
                appName={resourceRollupValue?.app?.displayName}
            />,
    },
    {
        field: 'entitlements',
        label: <FormattedMessage id="components_resource_rollup_list_entitlements" defaultMessage="Entitlements" />,
        Component: ({ row: resourceRollupValue }): React.ReactNode => {
            const ents = resourceRollupValue?.appEntitlements || []
            if (ents.length === 0) {
                return <C1EmptyIndicator />
            }
            return <EntitlementChips appEntitlements={ents} showGrantCount={false} />
        },
    },
    {
        field: 'accounts',
        label: <FormattedMessage id="components_resource_rollup_list_accounts" defaultMessage="Accounts" />,
        Component: ({ row: resourceRollupValue }): React.ReactNode => {
            if (
                !resourceRollupValue?.appEntitlements
                || resourceRollupValue.appEntitlements.length === 0
                || resourceRollupValue.appEntitlements[0]?.grantCount === undefined
            ) {
                return <C1EmptyIndicator />
            }

            return <Typography>
                <FormattedNumber value={resourceRollupValue.appEntitlements[0]?.grantCount} />
            </Typography>
        },
    },
]

export const Resources: FC = () => {
    const initialFilters = useInitialFilters(SearchAppResourceRollupRequest)

    const [emptyReq] = useState<Partial<SearchAppResourceRollupRequest>>({
        resourceIds: [],
    })

    const [filter, setFilter] = useState<SearchAppResourceRollupRequest>(
        SearchAppResourceRollupRequest.create({
            ...initialFilters,
            ...emptyReq,
        }),
    )

    const [page, setPage] = useState<number>(0)

    const { data, isLoading, isError, getPage, canLoadMore, error } =
        appResourceSearchRollupApi.useSearchAppResourcesRollup(filter)

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    useErrorReporting(isError, error, QUERIES.Resources)

    const { canClear, clear, onChange, onQueryChange } = useSearch(
        SearchAppResourceRollupRequest,
        filter,
        setFilter,
        emptyReq,
    )

    const onAppChange = useCallback(
        (selected: App[], _: boolean) => {
            onChange({ appIds: selected?.map((a) => a.id) })
        },
        [onChange],
    )

    const exportDataActionRequest = useMemo(() => {
        return ExportDataRequest.create({
            queryId: QueryId.RESOURCES,
            request: {
                oneofKind: 'searchAppResourceRollupRequest',
                searchAppResourceRollupRequest: SearchAppResourceRollupRequest.create({ ...filter }),
            },
        })
    }, [filter])

    const search = useMemo(() => <Stack
        direction="row"
        spacing={2}
        justifyContent="space-between"
        alignItems="flex-end"
        pr={1}
        sx={{
            backgroundColor: 'grey.50',
            width: '100%',
            p: 2,
            borderTopLeftRadius: C1BorderRadius.large,
            borderTopRightRadius: C1BorderRadius.large,
            border: '1px solid',
            borderColor: 'grey.150',
            borderBottom: 'none',
        }}
    >
        <AppResourceCampaignFilters
            filter={filter}
            canClear={canClear}
            clear={clear}
            onQueryChange={onQueryChange}
            onAppChange={onAppChange}
            onChange={onChange}
        />
        <ExportData exportDataRequest={exportDataActionRequest} />
    </Stack>, [filter, canClear, clear, onQueryChange, onAppChange, onChange, exportDataActionRequest])

    const table = useMemo(() => <C1TableRedux<ResourceRollupValue>
        data={data}
        cols={cols}
        canLoadMore={canLoadMore}
        isError={isError}
        isLoading={isLoading}
        onPageChange={onPageChange}
        error={error}
        page={page}
        getId={(row): string => `${row.app?.id}-${row.appResource?.id}`}
    />, [canLoadMore, data, error, isError, isLoading, onPageChange, page])

    return <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'auto',
            p: 1,
            maxHeight: 'calc(100vh - 200px)', // 60px header + padding
            '.MuiTableContainer-root': {
                mt: 0,
                borderTopLeftRadius: 0,
                borderTopRightRadius: 0,
            },
        }}
    >
        <List<ResourceRollupValue> search={search} table={table} />
    </Box>
}
